import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Users,
  UserPlus,
  Search,
  CheckSquare,
  Square,
  Trash2,
  Edit,
  RotateCcw,
  Plus,
  X,
  Mail,
  Building,
  Phone,
  Briefcase,
  CheckCircle,
  AlertCircle,
  FileSignature,
  ShieldCheck,
  UserCheck,
  UserMinus,
  Check,
} from 'lucide-react';
import { useContactStore, SavedContact } from '../stores/contactStore';
import { toast, confirmDialog } from '../stores/feedbackStore';
import { SignerRole } from '@pdfsign/shared/types';

const contactFormSchema = z.object({
  full_name: z.string().min(1, 'Họ và tên là bắt buộc').max(100),
  email: z.string().email('Email không đúng định dạng'),
  title: z.string().max(100).optional(),
  department: z.string().max(100).optional(),
  phone: z.string().max(20).optional(),
  role: z.enum(['signer', 'approver', 'cc', 'witness']),
  signature_type: z.enum(['draw', 'image', 'text', 'digital']),
});

type ContactFormData = z.infer<typeof contactFormSchema>;

const roleConfig: Record<SignerRole, { label: string; color: string; desc: string }> = {
  signer: {
    label: 'Người ký chính',
    color: 'bg-sky-50 dark:bg-sky-950/50 text-sky-600 dark:text-sky-400 border-sky-200 dark:border-sky-800/40',
    desc: 'Trực tiếp ký vào văn bản',
  },
  approver: {
    label: 'Người duyệt',
    color: 'bg-amber-50 dark:bg-amber-950/50 text-amber-600 dark:text-amber-400 border-amber-200 dark:border-amber-800/40',
    desc: 'Phê duyệt trước khi ký chính thức',
  },
  cc: {
    label: 'Bản sao (CC)',
    color: 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 border-slate-200 dark:border-white/10',
    desc: 'Nhận bản sao lưu để theo dõi',
  },
  witness: {
    label: 'Làm chứng',
    color: 'bg-emerald-50 dark:bg-emerald-950/50 text-emerald-600 dark:text-emerald-400 border-emerald-200 dark:border-emerald-800/40',
    desc: 'Xác nhận chứng kiến ký kết',
  },
};

const signatureTypeLabels: Record<string, string> = {
  draw: 'Vẽ chữ ký tay',
  image: 'Tải ảnh con dấu / chữ ký',
  text: 'Ký theo tên định dạng',
  digital: 'Chứng thư số (PKI)',
};

export default function ContactsPage() {
  const {
    contacts,
    addContact,
    updateContact,
    deleteContact,
    deleteMultipleContacts,
    resetDefaultContacts,
  } = useContactStore();

  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState<string>('all');
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingContact, setEditingContact] = useState<SavedContact | null>(null);

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm<ContactFormData>({
    resolver: zodResolver(contactFormSchema),
    defaultValues: {
      role: 'signer',
      signature_type: 'draw',
    },
  });

  const handleOpenAdd = () => {
    setEditingContact(null);
    reset({
      full_name: '',
      email: '',
      title: '',
      department: '',
      phone: '',
      role: 'signer',
      signature_type: 'draw',
    });
    setModalOpen(true);
  };

  const handleOpenEdit = (contact: SavedContact) => {
    setEditingContact(contact);
    reset({
      full_name: contact.full_name,
      email: contact.email,
      title: contact.title || '',
      department: contact.department || '',
      phone: contact.phone || '',
      role: contact.role,
      signature_type: contact.signature_type,
    });
    setModalOpen(true);
  };

  const onSubmit = (data: ContactFormData) => {
    if (editingContact) {
      updateContact(editingContact.id, data);
      toast.success(`Đã cập nhật thông tin cho "${data.full_name}"`);
    } else {
      addContact(data);
      toast.success(`Đã thêm "${data.full_name}" vào danh bạ người ký`);
    }
    setModalOpen(false);
  };

  const handleDelete = async (id: string, name: string) => {
    const ok = await confirmDialog({
      title: 'Xóa người ký khỏi danh bạ',
      message: `Bạn có chắc chắn muốn xóa "${name}" khỏi danh bạ?`,
      confirmText: 'Xóa',
      cancelText: 'Hủy',
      type: 'danger',
    });
    if (ok) {
      deleteContact(id);
      setSelectedIds((prev) => prev.filter((i) => i !== id));
      toast.success(`Đã xóa "${name}" khỏi danh bạ`);
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedIds.length === 0) return;
    const ok = await confirmDialog({
      title: 'Xóa nhiều người ký',
      message: `Bạn có chắc chắn muốn xóa ${selectedIds.length} người ký đã chọn?`,
      confirmText: 'Xóa tất cả',
      cancelText: 'Hủy',
      type: 'danger',
    });
    if (ok) {
      deleteMultipleContacts(selectedIds);
      setSelectedIds([]);
      toast.success(`Đã xóa ${selectedIds.length} người ký khỏi danh bạ`);
    }
  };

  const handleResetDefaults = async () => {
    const ok = await confirmDialog({
      title: 'Khôi phục danh bạ mặc định',
      message:
        'Khôi phục danh bạ về mẫu cán bộ mặc định (Huỳnh Thị Thu Hằng, Nguyễn Bùi Ngọc Ánh, Trương Thị Thùy Trâm...)?',
      confirmText: 'Khôi phục',
      cancelText: 'Hủy',
      type: 'warning',
    });
    if (ok) {
      resetDefaultContacts();
      setSelectedIds([]);
      toast.success('Đã khôi phục danh bạ mẫu mặc định');
    }
  };

  const filteredContacts = contacts.filter((c) => {
    const matchesSearch =
      !search ||
      c.full_name.toLowerCase().includes(search.toLowerCase()) ||
      c.email.toLowerCase().includes(search.toLowerCase()) ||
      (c.title && c.title.toLowerCase().includes(search.toLowerCase())) ||
      (c.department && c.department.toLowerCase().includes(search.toLowerCase()));

    const matchesRole = roleFilter === 'all' || c.role === roleFilter;
    return matchesSearch && matchesRole;
  });

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  };

  const toggleSelectAll = () => {
    if (selectedIds.length === filteredContacts.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(filteredContacts.map((c) => c.id));
    }
  };

  // Counters
  const countSigners = contacts.filter((c) => c.role === 'signer').length;
  const countApprovers = contacts.filter((c) => c.role === 'approver').length;
  const countCC = contacts.filter((c) => c.role === 'cc').length;

  return (
    <div className="space-y-6">
      {/* Top Banner / Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <div className="flex items-center gap-2.5">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-sky-400 to-indigo-600 flex items-center justify-center text-white shadow-md shadow-sky-500/20">
              <Users className="w-5 h-5" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
                <span>Danh bạ người ký</span>
                <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-sky-100 dark:bg-sky-950/60 text-sky-600 dark:text-sky-400">
                  {contacts.length} người
                </span>
              </h1>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                Quản lý danh sách cán bộ, người ký thẩm định & phê duyệt theo dõi quy trình ký số
              </p>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={handleResetDefaults}
            className="btn-outline h-9 text-xs px-3 font-normal gap-1.5"
            title="Khôi phục danh sách mẫu cán bộ"
          >
            <RotateCcw className="w-3.5 h-3.5 text-slate-400" />
            <span className="hidden sm:inline">Khôi phục mẫu</span>
          </button>
          <button
            onClick={handleOpenAdd}
            className="btn-primary h-9 text-xs px-3.5 font-medium gap-1.5 shadow-sm"
          >
            <UserPlus className="w-4 h-4" />
            <span>Thêm người ký</span>
          </button>
        </div>
      </div>

      {/* Stat Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="card p-3.5 flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-sky-50 dark:bg-sky-950/50 text-sky-600 dark:text-sky-400 flex items-center justify-center">
            <Users className="w-4 h-4" />
          </div>
          <div>
            <div className="text-[11px] text-slate-400 font-normal">Tổng danh bạ</div>
            <div className="text-lg font-bold text-slate-900 dark:text-white leading-tight">
              {contacts.length}
            </div>
          </div>
        </div>

        <div className="card p-3.5 flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-indigo-50 dark:bg-indigo-950/50 text-indigo-600 dark:text-indigo-400 flex items-center justify-center">
            <FileSignature className="w-4 h-4" />
          </div>
          <div>
            <div className="text-[11px] text-slate-400 font-normal">Người ký chính</div>
            <div className="text-lg font-bold text-slate-900 dark:text-white leading-tight">
              {countSigners}
            </div>
          </div>
        </div>

        <div className="card p-3.5 flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-amber-50 dark:bg-amber-950/50 text-amber-600 dark:text-amber-400 flex items-center justify-center">
            <ShieldCheck className="w-4 h-4" />
          </div>
          <div>
            <div className="text-[11px] text-slate-400 font-normal">Người duyệt</div>
            <div className="text-lg font-bold text-slate-900 dark:text-white leading-tight">
              {countApprovers}
            </div>
          </div>
        </div>

        <div className="card p-3.5 flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 flex items-center justify-center">
            <Mail className="w-4 h-4" />
          </div>
          <div>
            <div className="text-[11px] text-slate-400 font-normal">Nhận bản sao (CC)</div>
            <div className="text-lg font-bold text-slate-900 dark:text-white leading-tight">
              {countCC}
            </div>
          </div>
        </div>
      </div>

      {/* Filter & Search Bar */}
      <div className="card p-4 space-y-3">
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3">
          {/* Search box */}
          <div className="relative flex-1">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Tìm kiếm theo họ tên, email, chức vụ, phòng ban..."
              className="input pl-9 h-9 text-xs"
            />
            {search && (
              <button
                onClick={() => setSearch('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>

          {/* Role Filter & Batch Delete */}
          <div className="flex items-center gap-2">
            <select
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
              className="input h-9 text-xs py-0 px-2.5 bg-white dark:bg-slate-900 min-w-[140px]"
            >
              <option value="all">Tất cả vai trò</option>
              <option value="signer">Người ký chính</option>
              <option value="approver">Người duyệt</option>
              <option value="cc">Bản sao (CC)</option>
              <option value="witness">Làm chứng</option>
            </select>

            {selectedIds.length > 0 && (
              <button
                onClick={handleDeleteSelected}
                className="btn-danger h-9 text-xs px-3 font-normal gap-1.5 animate-fade-in"
              >
                <Trash2 className="w-3.5 h-3.5" />
                <span>Xóa {selectedIds.length} mục</span>
              </button>
            )}
          </div>
        </div>

        {/* Selected Counter & Multi-select Bar */}
        <div className="flex items-center justify-between pt-2 border-t border-slate-100 dark:border-white/5 text-xs text-slate-500 dark:text-slate-400">
          <div className="flex items-center gap-2">
            <button
              onClick={toggleSelectAll}
              className="flex items-center gap-1.5 hover:text-sky-500 transition-colors"
            >
              {selectedIds.length === filteredContacts.length && filteredContacts.length > 0 ? (
                <CheckSquare className="w-4 h-4 text-sky-500" />
              ) : (
                <Square className="w-4 h-4 text-slate-400" />
              )}
              <span>
                {selectedIds.length === filteredContacts.length && filteredContacts.length > 0
                  ? 'Bỏ chọn tất cả'
                  : 'Chọn tất cả'}
              </span>
            </button>
            {selectedIds.length > 0 && (
              <span className="text-sky-500 font-medium">
                (Đã chọn {selectedIds.length}/{filteredContacts.length})
              </span>
            )}
          </div>

          <div>
            Hiển thị <strong>{filteredContacts.length}</strong> / {contacts.length} người ký
          </div>
        </div>
      </div>

      {/* Contacts List / Table */}
      <div className="card overflow-hidden border border-slate-200 dark:border-white/10 shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse text-xs">
            <thead>
              <tr className="bg-slate-50/80 dark:bg-slate-900/60 border-b border-slate-200 dark:border-white/10 text-slate-500 dark:text-slate-400 font-medium uppercase tracking-wider text-[11px]">
                <th className="p-3.5 w-10 text-center">
                  <span className="sr-only">Chọn</span>
                </th>
                <th className="p-3.5">Họ và tên</th>
                <th className="p-3.5">Email & Liên hệ</th>
                <th className="p-3.5">Đơn vị / Phòng ban</th>
                <th className="p-3.5">Vai trò mặc định</th>
                <th className="p-3.5">Hình thức ký</th>
                <th className="p-3.5 text-right w-24">Thao tác</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-white/5">
              {filteredContacts.map((contact) => {
                const isSelected = selectedIds.includes(contact.id);
                const role = roleConfig[contact.role] || roleConfig.signer;
                const initials = contact.full_name
                  .split(' ')
                  .filter(Boolean)
                  .slice(-2)
                  .map((w) => w[0]?.toUpperCase())
                  .join('') || 'NK';

                return (
                  <tr
                    key={contact.id}
                    className={`transition-colors hover:bg-slate-50/70 dark:hover:bg-slate-900/40 ${
                      isSelected ? 'bg-sky-50/50 dark:bg-sky-950/20' : ''
                    }`}
                  >
                    {/* Checkbox */}
                    <td className="p-3.5 text-center">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleSelect(contact.id)}
                        className="w-4 h-4 rounded text-sky-600 focus:ring-sky-500 border-slate-300 dark:border-slate-600 dark:bg-slate-800 cursor-pointer"
                      />
                    </td>

                    {/* Name + Initials */}
                    <td className="p-3.5">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-sky-400 to-indigo-600 text-white flex items-center justify-center font-bold text-xs flex-shrink-0 shadow-sm">
                          {initials}
                        </div>
                        <div>
                          <div className="font-semibold text-slate-900 dark:text-white">
                            {contact.full_name}
                          </div>
                          {contact.title && (
                            <div className="text-[11px] text-slate-400 font-normal">
                              {contact.title}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>

                    {/* Email & Phone */}
                    <td className="p-3.5 font-normal">
                      <div className="text-slate-700 dark:text-slate-300 flex items-center gap-1.5">
                        <Mail className="w-3 h-3 text-slate-400 flex-shrink-0" />
                        <span>{contact.email}</span>
                      </div>
                      {contact.phone && (
                        <div className="text-[11px] text-slate-400 flex items-center gap-1.5 mt-0.5">
                          <Phone className="w-3 h-3 text-slate-400 flex-shrink-0" />
                          <span>{contact.phone}</span>
                        </div>
                      )}
                    </td>

                    {/* Department */}
                    <td className="p-3.5 font-normal text-slate-600 dark:text-slate-400">
                      {contact.department ? (
                        <div className="flex items-center gap-1.5">
                          <Building className="w-3 h-3 text-slate-400 flex-shrink-0" />
                          <span>{contact.department}</span>
                        </div>
                      ) : (
                        <span className="text-slate-400 dark:text-slate-600 italic">—</span>
                      )}
                    </td>

                    {/* Default Role */}
                    <td className="p-3.5">
                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium border ${role.color}`}
                        title={role.desc}
                      >
                        {role.label}
                      </span>
                    </td>

                    {/* Signature Type */}
                    <td className="p-3.5 font-normal text-slate-600 dark:text-slate-400 text-[11px]">
                      {signatureTypeLabels[contact.signature_type] || contact.signature_type}
                    </td>

                    {/* Actions */}
                    <td className="p-3.5 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => handleOpenEdit(contact)}
                          className="p-1.5 text-slate-400 hover:text-sky-500 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors"
                          title="Chỉnh sửa thông tin"
                        >
                          <Edit className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={() => handleDelete(contact.id, contact.full_name)}
                          className="p-1.5 text-slate-400 hover:text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-950/30 rounded-lg transition-colors"
                          title="Xóa khỏi danh bạ"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}

              {filteredContacts.length === 0 && (
                <tr>
                  <td colSpan={7} className="p-8 text-center text-slate-400 text-xs">
                    <Users className="w-8 h-8 mx-auto mb-2 opacity-30" />
                    <p className="font-medium text-slate-600 dark:text-slate-300">
                      Không tìm thấy người ký nào
                    </p>
                    <p className="text-[11px] text-slate-400 mt-1">
                      Thử tìm kiếm với từ khóa khác hoặc bấm nút thêm người ký mới
                    </p>
                    <button
                      onClick={handleOpenAdd}
                      className="btn-outline h-8 text-xs mt-3 font-normal"
                    >
                      + Thêm người ký vào danh bạ
                    </button>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add / Edit Contact Modal */}
      {modalOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm animate-fade-in"
          onClick={() => setModalOpen(false)}
        >
          <div
            className="bg-white dark:bg-[#111827] rounded-2xl shadow-2xl border border-slate-200 dark:border-white/10 max-w-lg w-full overflow-hidden animate-slide-up flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="p-4 sm:p-5 border-b border-slate-100 dark:border-white/10 flex items-center justify-between">
              <div>
                <h2 className="text-base font-semibold text-slate-900 dark:text-white flex items-center gap-2">
                  <UserPlus className="w-4 h-4 text-sky-500" />
                  <span>{editingContact ? 'Chỉnh sửa người ký' : 'Thêm người ký vào danh bạ'}</span>
                </h2>
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                  Lưu thông tin để tự động gợi ý và tích chọn nhanh khi gửi văn bản ký
                </p>
              </div>
              <button
                onClick={() => setModalOpen(false)}
                className="p-1.5 text-slate-400 hover:text-slate-600 dark:hover:text-white rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Modal Form */}
            <form onSubmit={handleSubmit(onSubmit)} className="p-4 sm:p-5 space-y-3.5">
              <div>
                <label className="label text-[11px] font-normal">
                  Họ và tên người ký <span className="text-rose-500">*</span>
                </label>
                <input
                  {...register('full_name')}
                  type="text"
                  placeholder="Ví dụ: Huỳnh Thị Thu Hằng"
                  className={`input h-8 text-xs font-normal ${errors.full_name ? 'input-error' : ''}`}
                />
                {errors.full_name && (
                  <p className="mt-1 text-[11px] text-rose-500">{errors.full_name.message}</p>
                )}
              </div>

              <div>
                <label className="label text-[11px] font-normal">
                  Email nhận tài liệu <span className="text-rose-500">*</span>
                </label>
                <input
                  {...register('email')}
                  type="email"
                  placeholder="hang.huynh@coquan.gov.vn"
                  className={`input h-8 text-xs font-normal ${errors.email ? 'input-error' : ''}`}
                />
                {errors.email && (
                  <p className="mt-1 text-[11px] text-rose-500">{errors.email.message}</p>
                )}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="label text-[11px] font-normal">Chức vụ / Tiêu đề</label>
                  <input
                    {...register('title')}
                    type="text"
                    placeholder="Ví dụ: Trưởng khoa / Chuyên viên"
                    className="input h-8 text-xs font-normal"
                  />
                </div>
                <div>
                  <label className="label text-[11px] font-normal">Phòng ban / Đơn vị</label>
                  <input
                    {...register('department')}
                    type="text"
                    placeholder="Ví dụ: Khoa Dược / Kế hoạch"
                    className="input h-8 text-xs font-normal"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="label text-[11px] font-normal">Vai trò mặc định</label>
                  <select {...register('role')} className="input h-8 text-xs font-normal py-0">
                    <option value="signer">Người ký chính</option>
                    <option value="approver">Người duyệt trước khi ký</option>
                    <option value="cc">Nhận bản sao (CC)</option>
                    <option value="witness">Người làm chứng</option>
                  </select>
                </div>
                <div>
                  <label className="label text-[11px] font-normal">Hình thức ký</label>
                  <select {...register('signature_type')} className="input h-8 text-xs font-normal py-0">
                    <option value="draw">Vẽ chữ ký tay</option>
                    <option value="image">Tải ảnh con dấu / chữ ký</option>
                    <option value="text">Ký theo tên định dạng</option>
                    <option value="digital">Chứng thư số (PKI / Token)</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="label text-[11px] font-normal">Số điện thoại (tùy chọn)</label>
                <input
                  {...register('phone')}
                  type="text"
                  placeholder="0901234567"
                  className="input h-8 text-xs font-normal"
                />
              </div>

              {/* Modal Buttons */}
              <div className="flex gap-2 pt-3 border-t border-slate-100 dark:border-white/10">
                <button
                  type="button"
                  onClick={() => setModalOpen(false)}
                  className="btn-outline flex-1 text-xs h-8 font-normal flex items-center justify-center gap-1.5"
                >
                  <X className="w-3.5 h-3.5" />
                  <span>Hủy</span>
                </button>
                <button type="submit" className="btn-primary flex-1 text-xs h-8 font-medium flex items-center justify-center gap-1.5">
                  <Check className="w-3.5 h-3.5" />
                  <span>{editingContact ? 'Lưu' : 'Thêm'}</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
